/**
 * Status presentation for the Comped Tickets table. The backend exposes the
 * claim state as the `transaction_type` enum; this maps each value to a
 * venue-manager-facing label key + a `<PunchupTag size="sm" />` background.
 *
 * Why hex literals (not CSS variables): `PunchupTag` resolves its background via
 * CSS `color-mix()`, which only accepts a concrete colour at parse time. These
 * mirror the status-colour taxonomy in `src/styles/CLAUDE.md`; if a token
 * changes, update the corresponding hex here. Mirrors the sibling helper in
 * `CustomFieldsResponses/components/PunchupResponsesTable/statusColors.ts`.
 */

const COLOR_INFO = '#3b82f6'; // blue — Pending (invite sent, not yet claimed) / Transfer Pending / Claimed (Transferred)
const COLOR_COMPED = '#14b8a6'; // teal — Claimed (recipient accepted the comp) / Claimed (Transfer Canceled)
const COLOR_WARNING = '#FACC15'; // yellow — Canceled (invite withdrawn)
const COLOR_INACTIVE = '#787B7E'; // grey — Unknown (claim state not reported by the API)

/** A comp is cancellable only while it is still an unclaimed invite (`for_comp`). */
export const isCancellableComp = (transactionType: string): boolean => transactionType === 'for_comp';

/**
 * i18n key + fallback for each comped claim state.
 *
 * `/orders?comped=true` returns a comped ticket's whole transaction chain, so a comp whose ticket was
 * handed on surfaces its downstream transfer rows here too. They are framed as comp states (the
 * recipient still holds a comp, it just moved) so a transferred comp never reads as a bare
 * "Transferred" — which, on a comp ledger, looks like something went wrong — nor falls to the
 * "Unknown" fallback. Canceling a transfer rolls the ticket back to its comp predecessor (ticketing-api
 * `getTicketPrevTransaction`), so the original recipient holds a claimed comp again — `transfer_canceled`
 * is a claimed state (teal), NOT a cancellation (that is `comp_canceled`, the withdrawn invite).
 */
export const COMP_STATUS_LABEL: Record<string, { key: string; fallback: string }> = {
	for_comp: { key: 'compedTickets-status-pending', fallback: 'Pending' },
	comped: { key: 'compedTickets-status-claimed', fallback: 'Claimed' },
	comp_canceled: { key: 'compedTickets-status-canceled', fallback: 'Canceled' },
	for_transfer: { key: 'compedTickets-status-transfer-pending', fallback: 'Transfer Pending' },
	transferred: { key: 'compedTickets-status-transferred', fallback: 'Claimed (Transferred)' },
	transfer_canceled: { key: 'compedTickets-status-transfer-canceled', fallback: 'Claimed (Transfer Canceled)' },
};

/**
 * Neutral fallback for a row whose `transaction_type` the API did not populate
 * with a recognised claim state. Labelled "Unknown" on the grey/inactive
 * background — the word matches the grey "claim state not reported" semantics
 * and never reads as a conclusive state — so the cell is never blank. This is a
 * UI cover for an upstream API gap; remove it once the backend always returns a
 * recognised `transaction_type`.
 */
const COMP_STATUS_FALLBACK = { key: 'compedTickets-status-unknown', fallback: 'Unknown' };

/**
 * Label key + fallback for a comped `transaction_type`, defaulting to the
 * neutral "Unknown" state for unrecognised/empty values so the status cell is
 * never blank. Pair with `getCompStatusBackground` (grey for the same default).
 */
export const getCompStatusLabel = (transactionType: string): { key: string; fallback: string } =>
	COMP_STATUS_LABEL[transactionType] ?? COMP_STATUS_FALLBACK;

/** Tag background for a comped `transaction_type`. */
export const getCompStatusBackground = (transactionType: string): string => {
	switch (transactionType) {
		case 'for_comp':
		case 'for_transfer':
		case 'transferred':
			return COLOR_INFO;
		case 'comped':
		case 'transfer_canceled':
			return COLOR_COMPED;
		case 'comp_canceled':
			return COLOR_WARNING;
		default:
			return COLOR_INACTIVE;
	}
};
